/**
 * Der freie Anzeigezeitraum im Popover am Desktop. Der Name des Auslösers nennt den sichtbaren
 * Zeitraum mit — Vorleser hören, was gerade zu sehen ist.
 */
import * as Popover from '@radix-ui/react-popover';
import { useState, type ReactNode } from 'react';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { ViewRangeCalendar, type IsoRange } from './ViewRangeCalendar';

export { MAX_VIEW_DAYS } from './ViewRangeCalendar';

interface Props {
  from: string;
  to: string;
  /** Der sichtbare Zeitraum; er steht auch im Namen des Knopfs. */
  label: string;
  icon?: ReactNode;
  /** Aussehen des Auslösers; Standard ist das Feld in einer hellen Werkzeugleiste. */
  triggerClassName?: string;
  onApply: (range: IsoRange) => void;
}

export function ViewRangePopover({ from, to, label, icon, triggerClassName, onApply }: Props) {
  const mobile = useMediaQuery('(max-width: 767px)');
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={
            triggerClassName ??
            'border-line/70 hover:bg-card-alt text-ink flex min-h-11 items-center gap-2 border-x px-3'
          }
          aria-label={`Zeitraum wählen: ${label}`}
        >
          {icon}
          <span className="whitespace-nowrap text-xs font-semibold">{label}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          collisionPadding={16}
          aria-label="Zeitraum wählen"
          className="bg-card border-line shadow-panel rounded-tile z-50 max-h-[85dvh] max-w-[calc(100vw-32px)] overflow-y-auto border p-5"
        >
          <ViewRangeCalendar
            initial={{ from, to }}
            months={mobile ? 1 : 2}
            onCancel={() => setOpen(false)}
            onApply={(range) => {
              onApply(range);
              setOpen(false);
            }}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
